import * as React from 'react'
import type { EntityResolution, TaggedValue } from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../lib/utils'
import { ConflictColumnHead } from './ConflictColumnHead'
import { ConflictCustomValueSummary, ConflictValueEditor } from './ConflictValueEditor'
import { ConflictValueCell } from './ConflictValueCell'
import {
  conflictColumnSelected,
  conflictFieldSide,
  conflictFieldValue,
  taggedScalarText,
  type ConflictPropertyRow,
  type ConflictSide,
  type ConflictStructuralReason
} from './conflict-view-model'
import type { ConflictValueEditorState } from './ConflictValueEditor'

/**
 * The labels row of a node conflict: each present side's sorted label set
 * (undefined = that side removed the entity), shown above the property rows
 * whenever the versions don't all agree.
 */
export interface ConflictLabelsRow {
  base?: string
  ours?: string
  theirs?: string
  /** The ours-vs-theirs sets diverge — the case that forces whole-entity. */
  differs: boolean
}

/** Why per-field picking is unavailable, spelled out for the operator. */
function structuralMessage(reason: ConflictStructuralReason | undefined): string {
  switch (reason) {
    case 'removed':
      return 'One side removed this entity — pick which side wins.'
    case 'labels':
      return 'The sides disagree on labels — resolve whole-entity.'
    case 'identity':
      return "The sides disagree on the relationship's type or endpoints — resolve whole-entity."
    case 'undecodable':
      return "This entity's properties could not be decoded — resolve whole-entity."
    default:
      return 'Both sides carry identical content — pick either side.'
  }
}

/** A labels-row cell; an empty label set reads "(none)" rather than blank. */
function labelCell(text: string): { kind: 'value'; text: string } {
  return { kind: 'value', text: text.length > 0 ? text : '(none)' }
}

export function ConflictDiffGrid({
  rows,
  labelsRow,
  targetName,
  sourceName,
  choice,
  wholeEntityOnly,
  structuralReason,
  interactive,
  showUnchanged,
  editing,
  onToggleUnchanged,
  onChooseSide,
  onChooseField,
  onChooseFieldValue,
  onOpenEditor,
  onEditingChange,
  onConfirmEditor,
  onCancelEditor
}: {
  rows: ConflictPropertyRow[]
  labelsRow?: ConflictLabelsRow
  targetName: string
  sourceName: string
  choice?: EntityResolution
  wholeEntityOnly: boolean
  structuralReason?: ConflictStructuralReason
  interactive: boolean
  showUnchanged: boolean
  editing: ConflictValueEditorState | null
  onToggleUnchanged: () => void
  onChooseSide?: (side: ConflictSide) => void
  onChooseField?: (field: string, side: ConflictSide) => void
  onChooseFieldValue?: (field: string, value: TaggedValue) => void
  onOpenEditor: (field: string, kind: ConflictValueEditorState['kind'], text: string) => void
  onEditingChange: (editing: ConflictValueEditorState) => void
  onConfirmEditor: () => void
  onCancelEditor: () => void
}) {
  // Only the conflicting properties are shown by default; the agreed ones
  // (identical on both sides — nothing to decide) collapse behind a dropdown.
  const shown = rows.filter((r) => r.differs)
  const unchanged = rows.filter((r) => !r.differs)
  const conflictingKeys = shown.map((r) => r.key)

  const headerSelect = (side: ConflictSide) =>
    interactive && onChooseSide ? () => onChooseSide(side) : undefined

  // Per-cell selection. A whole-entity-only conflict picks the whole side from
  // any cell; otherwise a conflicting field's cell picks that one field. The
  // chosen side stays highlighted while submitting (disabled) — only the ability
  // to change it (onSelect) is withdrawn — so a merge in flight still shows what
  // was confirmed. Agreed fields are not pickable; they merge as-is.
  const cell = (key: string, side: ConflictSide, differs: boolean) => {
    if (wholeEntityOnly) {
      return {
        selected: choice === side,
        onSelect: interactive && onChooseSide ? () => onChooseSide(side) : undefined
      }
    }
    if (!differs || !onChooseField) return {}
    return {
      selected: conflictFieldSide(choice, key) === side,
      onSelect: interactive ? () => onChooseField(key, side) : undefined,
      ariaLabel: `${side === 'ours' ? `Keep ${targetName}` : `Take ${sourceName}`} — ${key}`
    }
  }

  return (
    <div className="grid grid-cols-[max-content_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_max-content] gap-x-3 px-2.5 py-2 text-11">
      <span />
      <ConflictColumnHead>Base</ConflictColumnHead>
      <ConflictColumnHead
        selected={conflictColumnSelected({
          choice,
          conflictingKeys,
          wholeEntityOnly,
          side: 'ours'
        })}
        onSelect={headerSelect('ours')}
      >
        Keep {targetName}
      </ConflictColumnHead>
      <ConflictColumnHead
        selected={conflictColumnSelected({
          choice,
          conflictingKeys,
          wholeEntityOnly,
          side: 'theirs'
        })}
        onSelect={headerSelect('theirs')}
      >
        Take {sourceName}
      </ConflictColumnHead>
      <span />

      {labelsRow ? (
        <React.Fragment>
          <span
            className={cn(
              'flex items-center gap-1 self-center py-0.5 font-mono font-medium',
              labelsRow.differs ? 'text-st-warn' : 'text-ink-3'
            )}
          >
            {labelsRow.differs ? <span aria-hidden>≠</span> : null}
            labels
          </span>
          <ConflictValueCell
            cell={labelsRow.base === undefined ? { kind: 'removed' } : labelCell(labelsRow.base)}
            dim={!labelsRow.differs}
          />
          <ConflictValueCell
            cell={labelsRow.ours === undefined ? { kind: 'removed' } : labelCell(labelsRow.ours)}
            dim={!labelsRow.differs}
            selected={wholeEntityOnly ? choice === 'ours' : undefined}
            onSelect={
              wholeEntityOnly && interactive && onChooseSide
                ? () => onChooseSide('ours')
                : undefined
            }
          />
          <ConflictValueCell
            cell={
              labelsRow.theirs === undefined ? { kind: 'removed' } : labelCell(labelsRow.theirs)
            }
            dim={!labelsRow.differs}
            selected={wholeEntityOnly ? choice === 'theirs' : undefined}
            onSelect={
              wholeEntityOnly && interactive && onChooseSide
                ? () => onChooseSide('theirs')
                : undefined
            }
          />
          <span />
        </React.Fragment>
      ) : null}

      {shown.length === 0 && !labelsRow?.differs ? (
        <p className="col-span-5 pt-1 text-ink-3 italic">{structuralMessage(structuralReason)}</p>
      ) : shown.length === 0 ? null : (
        shown.map((r) => {
          const o = cell(r.key, 'ours', true)
          const t = cell(r.key, 'theirs', true)
          const value = conflictFieldValue(choice, r.key)
          const editKind = r.editKind
          return (
            <React.Fragment key={r.key}>
              <span className="flex items-center gap-1 self-center py-0.5 font-mono font-medium text-st-warn">
                <span aria-hidden>≠</span>
                {r.key}
              </span>
              <ConflictValueCell cell={r.base} />
              <ConflictValueCell
                cell={r.ours}
                selected={o.selected}
                onSelect={o.onSelect}
                ariaLabel={o.ariaLabel}
              />
              <ConflictValueCell
                cell={r.theirs}
                selected={t.selected}
                onSelect={t.onSelect}
                ariaLabel={t.ariaLabel}
              />
              {!wholeEntityOnly && editKind !== null && onChooseFieldValue ? (
                <button
                  type="button"
                  disabled={!interactive}
                  onClick={() =>
                    onOpenEditor(
                      r.key,
                      editKind,
                      value !== undefined ? taggedScalarText(value) : r.seedText
                    )
                  }
                  aria-label={`Edit value — ${r.key}`}
                  title="Use a custom value"
                  className={cn(
                    'self-center justify-self-end rounded-sm px-1 py-0.5',
                    value !== undefined ? 'text-brand' : 'text-ink-3',
                    interactive ? 'cursor-pointer hover:text-ink' : 'cursor-default'
                  )}
                >
                  <span aria-hidden>✎</span>
                </button>
              ) : (
                <span />
              )}

              {editing?.field === r.key ? (
                <ConflictValueEditor
                  editing={editing}
                  interactive={interactive}
                  onEditingChange={onEditingChange}
                  onConfirm={onConfirmEditor}
                  onCancel={onCancelEditor}
                />
              ) : value !== undefined ? (
                <ConflictCustomValueSummary
                  value={value}
                  interactive={interactive}
                  canEdit={editKind !== null && Boolean(onChooseFieldValue)}
                  onEdit={() => onOpenEditor(r.key, editKind!, taggedScalarText(value))}
                />
              ) : null}
            </React.Fragment>
          )
        })
      )}
      {unchanged.length > 0 ? (
        <>
          <button
            type="button"
            onClick={onToggleUnchanged}
            aria-expanded={showUnchanged}
            className="col-span-5 mt-0.5 flex cursor-pointer items-center gap-1 text-left text-10 text-ink-3 hover:text-ink"
          >
            <span aria-hidden="true">{showUnchanged ? '▾' : '▸'}</span>
            {unchanged.length} unchanged propert{unchanged.length !== 1 ? 'ies' : 'y'}
          </button>
          {showUnchanged
            ? unchanged.map((r) => (
                <React.Fragment key={r.key}>
                  <span className="self-center py-0.5 font-mono text-ink-3">{r.key}</span>
                  <ConflictValueCell cell={r.base} dim />
                  <ConflictValueCell cell={r.ours} dim />
                  <ConflictValueCell cell={r.theirs} dim />
                  <span />
                </React.Fragment>
              ))
            : null}
        </>
      ) : null}
    </div>
  )
}
